import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Heart, 
  MapPin, 
  Star, 
  Phone, 
  Globe, 
  ExternalLink,
  ArrowLeft,
  Loader2,
  BookOpen
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useRoleBasedApi } from '../hooks/useRoleBasedApi';
import { useToast } from '../hooks/use-toast';
import UnifiedMobileFooter from '../components/UnifiedMobileFooter';

interface Location {
  id: number;
  name: string;
  description: string;
  address: string;
  city: string;
  county: string;
  country: string;
  latitude: number;
  longitude: number;
  type: string;
  serviceTypes: string[];
  placeTypes: string[];
  experiences: string[];
  amenities: string[];
  images: string[];
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  bookingUrl?: string;
  rating?: number;
  price?: string;
  is_favorite?: boolean;
  opening_hours?: {
    [key: string]: string;
  };
  pricing?: Array<{
    name: string;
    price: number;
    duration: number;
  }>;
}

const LocationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [location, setLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  
  const { get, post, delete: deleteRequest } = useRoleBasedApi();
  const { toast } = useToast();

  // Fetch location details
  const fetchLocation = async () => {
    try {
      setLoading(true);
      setError('');
      
      const data = await get(`/locations/${id}`);
      setLocation(data.location);

    } catch (error) {
      console.error('Failed to fetch location:', error);
      setError('Failed to load location details');
    } finally {
      setLoading(false);
    }
  };

  // Toggle favorite


  useEffect(() => {
    if (id) {
      fetchLocation();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading location details...</span>
        </div>
      </div>
    );
  }

  if (error || !location) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertDescription>
            {error || 'Location not found'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{location.name} | SaunaDip</title>
        <meta name="description" content={location.description} />
      </Helmet>
      
      <div className="container mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Location Header */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="h-5 w-5 text-gray-500" />
                      <h1 className="text-2xl sm:text-3xl font-bold">{location.name}</h1>

                    </div>
                    
                    <p className="text-gray-600 mb-3">{location.address}</p>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{location.city}, {location.county}</span>
                      {location.rating && (
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          <span>{location.rating}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleFavorite}
                    disabled={isFavoriting}
                    className={`${isFavorite ? 'text-red-600 hover:text-red-700 hover:bg-red-50' : 'text-gray-600'}`}
                  >
                    {isFavoriting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Heart className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
                    )}
                  </Button>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button className="flex-1 bg-sauna-orange hover:bg-sauna-orange/90" asChild>
                    <Link to={`/log?location=${location.id}`}>
                      <BookOpen className="h-4 w-4 mr-2" />
                      Log Dip
                    </Link>
                  </Button>
                  
                  {location.bookingUrl && (
                    <Button variant="outline" className="flex-1" asChild>
                      <a href={location.bookingUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Book Now
                      </a>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle>About</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">{location.description}</p>
              </CardContent>
            </Card>

            {/* Amenities */}
            {location.amenities && location.amenities.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Amenities</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {location.amenities.map((amenity, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-sauna-orange rounded-full"></div>
                        <span className="text-sm">{amenity}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Services */}
            {location.serviceTypes && location.serviceTypes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Services</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {location.serviceTypes.map((service, index) => (
                      <Badge key={index} variant="secondary">
                        {service}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Experiences */}
            {location.experiences && location.experiences.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Experiences</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {location.experiences.map((experience, index) => (
                      <Badge key={index} variant="outline">
                        {experience}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Images */}
            {location.images && location.images.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Photos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {location.images.slice(0, 6).map((image, index) => (
                      <div key={index} className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                        <img 
                          src={image} 
                          alt={`${location.name} photo ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/placeholder.svg';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle>Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {location.contactPhone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <a 
                      href={`tel:${location.contactPhone}`}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {location.contactPhone}
                    </a>
                  </div>
                )}
                
                {location.contactEmail && (
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-gray-500" />
                    <a 
                      href={`mailto:${location.contactEmail}`}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {location.contactEmail}
                    </a>
                  </div>
                )}
                
                {location.website && (
                  <div className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4 text-gray-500" />
                    <a 
                      href={location.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Visit Website
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Location Type */}
            <Card>
              <CardHeader>
                <CardTitle>Type</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge variant="outline" className="text-sm">
                  {location.type}
                </Badge>
              </CardContent>
            </Card>

            {/* Pricing */}
            {location.pricing && location.pricing.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Pricing</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {location.pricing.map((price, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-sm">{price.name}</p>
                        <p className="text-xs text-gray-500">{price.duration} minutes</p>
                      </div>
                      <span className="font-semibold">€{price.price}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Opening Hours */}
            {location.opening_hours && Object.keys(location.opening_hours).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Opening Hours</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {Object.entries(location.opening_hours).map(([day, hours]) => (
                    <div key={day} className="flex justify-between text-sm">
                      <span className="capitalize">{day}</span>
                      <span>{hours}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      <UnifiedMobileFooter />
    </>
  );
};

export default LocationDetailPage; 